import { useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { usePermissions } from '@/hooks/use-permissions';
import { SettingsPage, SettingsTableHead, useTabParam, type SettingsTab } from '@/modules/settings';
import { EscalationLog, EscalationLogTools } from '@/modules/escalation-policy/escalation-log';
import { ESCALATION_LOG_COLUMNS } from '@/modules/escalation-policy/escalation-log-columns';
import { useEscalationLogFilters } from '@/modules/escalation-policy/use-escalation-log-filters';
import { AutomationLogsTools, SentRepliesTab } from './automation-sent-replies';
import { automationLogColumns, useAutomationLogFilters } from './automation-log-filters';
import { SettingsLog, SettingsLogTools } from './settings-log';
import { SETTINGS_LOG_COLUMNS } from './settings-log-columns';
import { DeviationGateNotice, DeviationLog, DeviationLogTools } from './deviation-log';
import { deviationLogColumns, useDeviationLogFilters } from './deviation-log-filters';
import type { DayRange } from '@/lib/automation-replies';

type LogsTab = 'automation' | 'escalations' | 'settings' | 'deviations';

const AUTOMATION_TAB: SettingsTab<LogsTab> = { value: 'automation', label: 'Automation' };
const ESCALATIONS_TAB: SettingsTab<LogsTab> = { value: 'escalations', label: 'Escalations' };
const SETTINGS_TAB: SettingsTab<LogsTab> = { value: 'settings', label: 'Settings' };
const DEVIATIONS_TAB: SettingsTab<LogsTab> = { value: 'deviations', label: 'Deviations' };

/**
 * Settings › Logs: what Kim did on its own, every escalation it ran, who changed the settings and, for
 * admins, how far each AI draft was from the agent's reply. `?tab=` opens a tab directly.
 */
export default function LogsPage() {
	const { can } = usePermissions();
	// Escalations and settings changes are brand settings; the page itself is gated on automation.
	const brandTabs = can('view', 'brand') ? [AUTOMATION_TAB, ESCALATIONS_TAB, SETTINGS_TAB] : [AUTOMATION_TAB];
	// Deviations read the shadow comparison, which is admin-only like the shadow report.
	const tabs = can('view', 'reports') ? [...brandTabs, DEVIATIONS_TAB] : brandTabs;
	const [tab, setTab] = useTabParam(tabs, 'automation');
	const automationFilters = useAutomationLogFilters();
	const escalationFilters = useEscalationLogFilters();
	const [settingsDays, setSettingsDays] = useState<DayRange>({});
	const deviationFilters = useDeviationLogFilters();
	const header = {
		automation: { tools: <AutomationLogsTools filters={automationFilters} />, columns: automationLogColumns(can('view', 'gap-report')) },
		escalations: { tools: <EscalationLogTools filters={escalationFilters} />, columns: ESCALATION_LOG_COLUMNS },
		settings: { tools: <SettingsLogTools days={settingsDays} onDaysChange={setSettingsDays} />, columns: SETTINGS_LOG_COLUMNS },
		deviations: {
			tools: <DeviationLogTools filters={deviationFilters} />,
			columns: deviationLogColumns(deviationFilters.sort, deviationFilters.setSort),
			notice: <DeviationGateNotice />
		}
	}[tab];

	return (
		<SettingsPage
			title='Logs'
			tabs={tabs}
			tab={tab}
			onTabChange={setTab}
			tools={header.tools}
			subHeader={
				<>
					{'notice' in header && header.notice}
					<SettingsTableHead columns={header.columns} />
				</>
			}
		>
			<TabsContent value='automation' className='mt-0'>
				<SentRepliesTab filters={automationFilters} />
			</TabsContent>
			<TabsContent value='escalations' className='mt-0'>
				<EscalationLog filters={escalationFilters} />
			</TabsContent>
			<TabsContent value='settings' className='mt-0'>
				<SettingsLog days={settingsDays} />
			</TabsContent>
			<TabsContent value='deviations' className='mt-0'>
				<DeviationLog filters={deviationFilters} />
			</TabsContent>
		</SettingsPage>
	);
}
